//LCADashboard.jsx
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, BarChart3, TrendingUp, Building2, Leaf, Zap, Loader2, AlertCircle, Edit3, Save, X, Grid3X3, Network, ArrowLeft, Target, Globe } from 'lucide-react';
import * as d3 from 'd3';
import { ExcelParser, getImpactCategoryData, getComponentsByCategory } from '../services/excelParser';
import { useToast } from '../hooks/use-toast';
import { mockLCAData } from '../data/mockLCAData';
import ComponentBreakdown from './ComponentBreakdown';
import InteractivePieChart from './InteractivePieChart';
import MatrixHeatmap from './MatrixHeatmap';
import LifeCycleChart from './LifeCycleChart';
import MaterialComparison from './MaterialComparison';
import SankeyDiagram from './SankeyDiagram';
import PVROIAnalysis from './PVROIAnalysis';
import Buildups from './Buildups';
import { useWallBuildups } from './lca/LibraryPanel';
import ProjectInfo from './ProjectInfo';
import ConstructionCapacity from './ConstructionCapacity';
import useScrollIndicator from '../hooks/useScrollIndicator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import CarbonIntensityLookup from './CarbonIntensityLookup';
import { getLatestGridIntensityForCountry } from '../services/gridIntensity';

const STORAGE_KEY = 'embodix_dashboard_state_v1';

// Reusable “Source ⓘ” badge with hover tooltip
const ROI_CARD_OPTIONS = [
  {
    id: 'pv-carbon',
    title: 'PV Carbon ROI',
    description: 'Track PV carbon payback, grid decarbonization assumptions, and embodied emissions.',
    icon: Zap,
    statusLabel: 'Live',
    disabled: false,
  },
  {
    id: 'pv-cost',
    title: 'PV Cost ROI',
    description: 'Financial ROI calculator to compare PV CAPEX vs operating savings.',
    icon: TrendingUp,
    statusLabel: 'Coming soon',
    disabled: true,
  },
  {
    id: 'storage',
    title: 'Building Carbon ROI',
    description: 'Asset-level carbon ROI to understand the carbon budget savings.',
    icon: Leaf,
    statusLabel: 'Coming soon',
    disabled: true,
  },
];

const LCADashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tab: tabParam } = useParams();
  const { clearSavedWalls } = useWallBuildups();
  const [selectedImpact, setSelectedImpact] = useState('gwp');
  const [lcaData, setLcaData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSampleDataNotification, setShowSampleDataNotification] = useState(false);
  const [isEditingBuildingInfo, setIsEditingBuildingInfo] = useState(false);
  const [editableBuildingInfo, setEditableBuildingInfo] = useState({
    name: '',
    area: '',
    city: '',
    country: '',
    buildingType: 'office',
    serviceLife: ''
  });
  const [requireBuildingInfoCompletion, setRequireBuildingInfoCompletion] = useState(false);
  const [showMissingFieldsMessage, setShowMissingFieldsMessage] = useState(false);
  const [showUploadSuccessAfterGating, setShowUploadSuccessAfterGating] = useState(false);
  const [isSavingBuildingInfo, setIsSavingBuildingInfo] = useState(false);
  const [rhinoDialogOpen, setRhinoDialogOpen] = useState(false);
  const [rhinoFileError, setRhinoFileError] = useState('');
  const [rhinoFileName, setRhinoFileName] = useState('');
  const [rhinoFileReady, setRhinoFileReady] = useState(false);
  const [rhinoFilePayload, setRhinoFilePayload] = useState([]);
  const [rhinoReminderOpen, setRhinoReminderOpen] = useState(false);
  const buildingInfoSaveTimeoutRef = useRef(null);
  const previousProjectIdRef = useRef(null);
  const energyPopupSnapshotRef = useRef(null);
  const [energyConfirmOpen, setEnergyConfirmOpen] = useState(false);
  const [energyPopupCloseNonce, setEnergyPopupCloseNonce] = useState(0);
  const [countryEntities, setCountryEntities] = useState([]);
  const [countryQuery, setCountryQuery] = useState('');
  const [showCountrySuggestions, setShowCountrySuggestions] = useState(false);
  const countrySuggestionsRef = useRef(null);
  const [gridIntensityLookupMeta, setGridIntensityLookupMeta] = useState(null);

  const getCityCountryFromLocation = (location) => {
    if (!location) return { city: '', country: '' };
    const trimmed = location.trim();
    if (!trimmed || trimmed.toLowerCase() === 'unknown') {
      return { city: '', country: '' };
    }

    const parts = trimmed.split(',').map((part) => part.trim()).filter(Boolean);
    if (parts.length >= 2) {
      return { city: parts[0], country: parts.slice(1).join(', ') };
    }
    return { city: '', country: parts[0] || '' };
  };

  const hasIncompleteBuildingInfo = (info) => {
    if (!info) return true;
    const required = [
      info.name,
      info.area,
      info.serviceLife,
      info.city,
      info.country,
      info.buildingType,
    ];
    return required.some((value) => !String(value ?? '').trim());
  };


  const formatBuildingType = (value) => {
    if (!value) return '';
    const readable = value.replace(/-/g, ' ').trim();
    if (!readable) return '';
    return readable.charAt(0).toUpperCase() + readable.slice(1);
  };

  const normalizeName = (value) => value?.trim().toLowerCase() || '';

  const ELECTRICITY_DATA_URL = 'https://ourworldindata.org/grapher/per-capita-electricity-source-stacked.csv?download-format=tab';

  const buildCountryEntities = (csvText) => {
    const rows = d3.csvParse(csvText);
    const map = new Map();
    rows.forEach((row) => {
      const name = (row.Entity || row.entity || '').trim();
      if (!name) return;
      if (!map.has(name)) {
        const code = (row.Code || row.code || '').trim();
        map.set(name, {
          name,
          code,
          nameLower: name.toLowerCase(),
          codeLower: code.toLowerCase(),
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  const renderBuildingInfoForm = (options = {}) => {
    const {
      hideCancel = false,
      primaryLabel = 'Save Changes',
      primaryButtonClass = 'bg-emerald-600 hover:bg-emerald-700 text-white',
      showInlineValidation = true,
      isSubmitting = false,
    } = options;
    const inputTextClasses = 'mt-1 text-[#0f0f0f] placeholder:text-[#9ca3af]';
    const selectTextClasses = 'mt-1 text-[#0f0f0f] data-[placeholder]:text-[#9ca3af]';
    return (
      <div className="space-y-4 text-left">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="building-name" className="text-sm font-medium text-gray-700">Building Name</Label>
            <Input
              id="building-name"
              value={editableBuildingInfo.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={inputTextClasses}
              placeholder="Enter building name"
            />
          </div>
          <div>
            <Label htmlFor="building-area" className="text-sm font-medium text-gray-700">Area (m²)</Label>
            <Input
              id="building-area"
              type="number"
              value={editableBuildingInfo.area}
              onChange={(e) => handleInputChange('area', e.target.value)}
              className={inputTextClasses}
              placeholder="Enter area"
            />
          </div>
          <div>
            <Label htmlFor="building-service-life" className="text-sm font-medium text-gray-700">Service Life (years)</Label>
            <Input
              id="building-service-life"
              type="number"
              value={editableBuildingInfo.serviceLife}
              onChange={(e) => handleInputChange('serviceLife', e.target.value)}
              className={inputTextClasses}
              placeholder="Enter service life"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="building-city" className="text-sm font-medium text-gray-700">City</Label>
            <Input
              id="building-city"
              value={editableBuildingInfo.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className={inputTextClasses}
              placeholder="Enter city"
            />
          </div>
          <div>
            <Label htmlFor="building-country" className="text-sm font-medium text-gray-700">Country</Label>
            <div className="relative mt-1" ref={countrySuggestionsRef}>
              <Input
                id="building-country"
                value={countryQuery}
                onChange={(e) => {
                  setCountryQuery(e.target.value);
                  handleInputChange('country', e.target.value);
                  setShowCountrySuggestions(true);
                }}
                onFocus={() => setShowCountrySuggestions(true)}
                className={inputTextClasses}
                placeholder="Type to search (e.g., Denmark)"
              />
              {showCountrySuggestions && filteredCountrySuggestions.length > 0 && (
                <ul className="absolute z-30 mt-2 max-h-60 w-full overflow-y-auto rounded-2xl border border-emerald-100 bg-white text-sm shadow-xl">
                  {filteredCountrySuggestions.map((entity) => (
                    <li key={entity.name}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-emerald-50"
                        onClick={() => handleCountrySelect(entity)}
                      >
                        <span className="text-[#2b4331]">{entity.name}</span>
                        {entity.code ? (
                          <span className="text-xs uppercase text-gray-500">{entity.code}</span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="building-type" className="text-sm font-medium text-gray-700">Building Type</Label>
            <Select
              value={editableBuildingInfo.buildingType}
              onValueChange={(value) => handleInputChange('buildingType', value)}
            >
              <SelectTrigger id="building-type" className={selectTextClasses}>
                <SelectValue placeholder="Select building type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="office">Office</SelectItem>
                <SelectItem value="retail">Retail</SelectItem>
                <SelectItem value="residential-mixed-use">Residential mixed use</SelectItem>
                <SelectItem value="hotel">Hotel</SelectItem>
                <SelectItem value="healthcare">Healthcare</SelectItem>
                <SelectItem value="others">Others</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-3 pt-2">
          <Button
            onClick={handleSaveBuildingInfo}
            size="sm"
            className={primaryButtonClass}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {primaryLabel}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-1" />
                {primaryLabel}
              </>
            )}
          </Button>
          {!hideCancel && (
            <Button
              onClick={handleCancelEditing}
              size="sm"
              variant="outline"
              className="text-[#0f0f0f] hover:text-[#0f0f0f]"
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
          )}
        </div>
        {showInlineValidation && showMissingFieldsMessage && (
          <p className="text-xs text-center text-red-600">
            Please fill in every field to continue.
          </p>
        )}
      </div>
    );
  };
  
  // Heatmap state persistence
  const [heatmapExpandMap, setHeatmapExpandMap] = useState(false);
  const [heatmapVisibleStages, setHeatmapVisibleStages] = useState(new Set(['a1', 'a2', 'a3', 'a4', 'a5', 'b4', 'b6', 'c3', 'c4', 'd']));
  const [heatmapHideValues, setHeatmapHideValues] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [preferLcaTabFirst, setPreferLcaTabFirst] = useState(false);
  const [activeRoiView, setActiveRoiView] = useState('menu');
  const [lifeCycleSummary, setLifeCycleSummary] = useState(null);
  const roiDetailCard = useMemo(
    () => ROI_CARD_OPTIONS.find((card) => card.id === activeRoiView),
    [activeRoiView]
  );
  const [energyUseKwhPerM2Yr, setEnergyUseKwhPerM2Yr] = useState('');
  const [gridIntensityKgPerKwh, setGridIntensityKgPerKwh] = useState('');
  const tabConfig = useMemo(
    () => ([
      { value: 'overview', label: 'Overview', icon: BarChart3 },
      { value: 'breakdown', label: 'Breakdown', icon: Building2 },
      { value: 'data-visualization', label: 'Data Visualization', icon: Grid3X3 },
      { value: 'pvroi', label: 'ROI', icon: Zap },
      { value: 'buildups', label: 'Buildups & Components', icon: Network },
      { value: 'lca', label: 'LCA', icon: Leaf },
    ]),
    []
  );
  const tabSlugToValue = useMemo(
    () => ({
      overview: 'overview',
      breakdown: 'breakdown',
      'data-visualization': 'data-visualization',
      roi: 'pvroi',
      buildups: 'buildups',
      lca: 'lca',
    }),
    []
  );
  const tabValueToSlug = useMemo(
    () => ({
      overview: '',
      breakdown: 'breakdown',
      'data-visualization': 'data-visualization',
      pvroi: 'roi',
      buildups: 'buildups',
      lca: 'lca',
    }),
    []
  );
  const orderedTabs = useMemo(() => {
    if (!preferLcaTabFirst) return tabConfig;
    const lcaTab = tabConfig.find((tab) => tab.value === 'lca');
    const rest = tabConfig.filter((tab) => tab.value !== 'lca');
    return lcaTab ? [lcaTab, ...rest] : rest;
  }, [preferLcaTabFirst, tabConfig]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!tabParam) {
      if (location.pathname === '/dashboard' || location.pathname === '/start') {
        if (activeTab !== 'overview') setActiveTab('overview');
      }
      if (location.pathname === '/lca') {
        if (activeTab !== 'lca') setActiveTab('lca');
      }
      return;
    }
    const mapped = tabSlugToValue[tabParam];
    if (mapped) {
      if (activeTab !== mapped) setActiveTab(mapped);
      return;
    }
    if (location.pathname.startsWith('/dashboard/')) {
      navigate('/dashboard', { replace: true });
    }
  }, [tabParam, tabSlugToValue, activeTab, navigate, location.pathname]);

  const handleTabChange = (value) => {
    if (value === 'lca') {
      navigate('/lca');
      return;
    }
    const slug = tabValueToSlug[value] ?? '';
    const target = slug ? `/dashboard/${slug}` : '/dashboard';
    if (location.pathname !== target) {
      navigate(target);
    }
  };

  const {
    containerRef: gwpScrollContainerRef,
    indicator: gwpScrollIndicator,
    update: updateGwpScrollIndicator,
  } = useScrollIndicator({ isEnabled: activeTab === 'overview' });

  // PV ROI logic moved into PVROIAnalysis component

  const { toast } = useToast();

  const currentImpactData = useMemo(() => {
    if (!lcaData || !lcaData.buildingInfo) return null;
    return getImpactCategoryData(lcaData, selectedImpact);
  }, [lcaData, selectedImpact]);

  const isEnergyComponent = useCallback((component) => {
    const category = String(component?.category || '').toLowerCase();
    const name = String(component?.name || '').toLowerCase();
    const subcategory = String(component?.subcategory || '').toLowerCase();
    return (
      category.includes('energy') ||
      subcategory.includes('b6') ||
      name.includes('operational energy') ||
      name === 'energy' ||
      name === 'b6'
    );
  }, []);

  const embodiedComponents = useMemo(() => {
    if (!lcaData?.components) return [];
    return lcaData.components.filter((component) => !isEnergyComponent(component));
  }, [lcaData, isEnergyComponent]);

  const embodiedImpactTotal = useMemo(() => {
    if (!embodiedComponents.length) return 0;
    return embodiedComponents.reduce((sum, component) => {
      return sum + (component.impacts?.[selectedImpact] || 0);
    }, 0);
  }, [embodiedComponents, selectedImpact]);

  const impactByCategoryData = useMemo(() => {
    if (!currentImpactData) return null;
    const total = embodiedImpactTotal;
    const data = embodiedComponents.map((component) => {
      const value = component.impacts?.[selectedImpact] || 0;
      return {
        name: component.name,
        category: component.category,
        value,
        percentage: total > 0 ? (value / total) * 100 : 0
      };
    });
    return {
      category: currentImpactData.category,
      data,
      total
    };
  }, [currentImpactData, embodiedComponents, embodiedImpactTotal, selectedImpact]);

  const energyIntensityForCrrem = useMemo(() => {
    const direct = Number(energyUseKwhPerM2Yr);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const info = lcaData?.buildingInfo || {};
    const candidates = [
      info.energyUseKwhPerM2Yr,
      info.energyIntensityKwhPerM2Yr,
      info.energyImpactKwhPerM2Yr,
      info.energyImpact,
      info.energyIntensity,
      info.energyUseKwhPerM2,
      info.energyUse
    ];
    for (const candidate of candidates) {
      const numeric = Number(candidate);
      if (Number.isFinite(numeric) && numeric > 0) return numeric;
    }
    return null;
  }, [lcaData, energyUseKwhPerM2Yr]);

  const handleEnergyPopupOpenChange = (open) => {
    if (open && lcaData?.isRhinoImport) {
      const info = lcaData?.buildingInfo || {};
      const candidates = [
        info.energyUseKwhPerM2Yr,
        info.energyIntensityKwhPerM2Yr,
        info.energyImpactKwhPerM2Yr,
        info.energyImpact,
        info.energyIntensity
      ];
      const nextValue = candidates
        .map((value) => Number(value))
        .find((value) => Number.isFinite(value) && value > 0);
      if (Number.isFinite(nextValue)) {
        setEnergyUseKwhPerM2Yr(String(nextValue));
      }
    }

    if (open) {
      energyPopupSnapshotRef.current = {
        energyUseKwhPerM2Yr,
        gridIntensityKgPerKwh
      };
    }
  };

  const handleEnergyPopupCloseRequest = () => {
    const snapshot = energyPopupSnapshotRef.current;
    if (!snapshot) return true;
    const changed =
      String(energyUseKwhPerM2Yr ?? '') !== String(snapshot.energyUseKwhPerM2Yr ?? '') ||
      String(gridIntensityKgPerKwh ?? '') !== String(snapshot.gridIntensityKgPerKwh ?? '');
    if (!changed) return true;

    setEnergyConfirmOpen(true);
    return false;
  };

  const handleEnergyConfirmSave = () => {
    setEnergyConfirmOpen(false);
    energyPopupSnapshotRef.current = null;
    setEnergyPopupCloseNonce((prev) => prev + 1);
  };

  const handleEnergyConfirmDiscard = () => {
    const snapshot = energyPopupSnapshotRef.current;
    if (snapshot) {
      setEnergyUseKwhPerM2Yr(snapshot.energyUseKwhPerM2Yr ?? '');
      setGridIntensityKgPerKwh(snapshot.gridIntensityKgPerKwh ?? '');
    }
    setEnergyConfirmOpen(false);
    energyPopupSnapshotRef.current = null;
    setEnergyPopupCloseNonce((prev) => prev + 1);
  };

  const componentsByCategory = useMemo(() => {
    if (!lcaData || !lcaData.components) return {};
    return getComponentsByCategory(lcaData.components);
  }, [lcaData]);

  const embodiedComponentsByCategory = useMemo(() => {
    if (!embodiedComponents.length) return {};
    return getComponentsByCategory(embodiedComponents);
  }, [embodiedComponents]);

  const impactPerM2YrDenom = useMemo(() => {
    if (!lcaData?.buildingInfo) return null;
    const area = Number(lcaData.buildingInfo.area || 0);
    const life = Number(lcaData.buildingInfo.serviceLife || 0);
    return area > 0 && life > 0 ? area * life : null;
  }, [lcaData]);

  const sampleCountry = useMemo(() => {
    if (!lcaData?.isSampleData) return '';
    return getCityCountryFromLocation(lcaData.buildingInfo?.location).country || '';
  }, [lcaData]);

  const embodiedOperationalChart = useMemo(() => {
    if (!currentImpactData || !lcaData?.buildingInfo) return null;
    const area = Number(lcaData.buildingInfo.area || 0);
    const life = Number(lcaData.buildingInfo.serviceLife || 0);
    const energyUse = Number(energyUseKwhPerM2Yr || 0);
    const gridIntensity = Number(gridIntensityKgPerKwh || 0);
    const operationalFromInputs =
      area > 0 && life > 0 && energyUse > 0 && gridIntensity > 0
        ? energyUse * gridIntensity * area * life
        : 0;
    const operationalFromStages = Object.values(lcaData?.lcaStagesData || {}).reduce(
      (sum, stage) => sum + Number(stage?.B6 || 0),
      0
    );
    const operational = operationalFromStages > 0 ? operationalFromStages : operationalFromInputs;
    const embodied = Math.max(0, Number(embodiedImpactTotal || 0));
    const total = embodied + operational;
    if (!(total > 0)) return null;
    const unit = currentImpactData.category?.unit || 'kg CO2-eq';
    return {
      category: {
        id: 'embodied_operational',
        name: 'Embodied vs Operational',
        unit,
        shortName: 'E/O'
      },
      data: [
        {
          name: 'Embodied',
          category: 'Embodied',
          value: embodied,
          percentage: (embodied / total) * 100,
          color: '#2f6046'
        },
        {
          name: 'Operational (B6)',
          category: 'Operational',
          value: operational,
          percentage: (operational / total) * 100,
          color: '#f59e0b'
        }
      ],
      total
    };
  }, [currentImpactData, embodiedImpactTotal, lcaData, energyUseKwhPerM2Yr, gridIntensityKgPerKwh]);

  const hasB6StageInLca = useMemo(() => {
    const stages = lcaData?.lcaStagesData;
    if (!stages || Object.keys(stages).length === 0) return false;
    return Object.values(stages).some((stage) => Number(stage?.B6) > 0);
  }, [lcaData]);

  const operationalImpactForB6 = useMemo(() => {
    if (!embodiedOperationalChart?.data || hasB6StageInLca) return 0;
    const operationalEntry = embodiedOperationalChart.data.find((entry) => entry.category === 'Operational');
    return Number(operationalEntry?.value || 0);
  }, [embodiedOperationalChart, hasB6StageInLca]);

  useEffect(() => {
    if (!lcaData?.isSampleData) return undefined;
    if (!energyUseKwhPerM2Yr) {
      setEnergyUseKwhPerM2Yr('150');
    }
    if (gridIntensityKgPerKwh || !sampleCountry) return undefined;

    let cancelled = false;
    getLatestGridIntensityForCountry(sampleCountry)
      .then((latest) => {
        if (cancelled || !latest) return;
        const roundedKg = Number(latest.valueKg.toFixed(3));
        setGridIntensityKgPerKwh(roundedKg.toFixed(3));
        setGridIntensityLookupMeta({
          countryName: latest.countryName,
          countryCode: latest.countryCode,
          year: latest.year,
          valueKg: roundedKg
        });
      })
      .catch((err) => {
        console.error('Failed to load grid intensity defaults', err);
      });

    return () => {
      cancelled = true;
    };
  }, [
    lcaData,
    energyUseKwhPerM2Yr,
    gridIntensityKgPerKwh,
    sampleCountry
  ]);

  useEffect(() => {
    const projectId = lcaData?.projectId;
    if (!projectId) return;

    const info = lcaData?.buildingInfo || {};
    const candidates = [
      info.energyUseKwhPerM2Yr,
      info.energyIntensityKwhPerM2Yr,
      info.energyImpactKwhPerM2Yr,
      info.energyImpact,
      info.energyIntensity
    ];
    const nextValue = candidates
      .map((value) => Number(value))
      .find((value) => Number.isFinite(value) && value > 0);

    if (lcaData?.isRhinoImport) {
      if (Number.isFinite(nextValue) && String(nextValue) !== String(energyUseKwhPerM2Yr ?? '')) {
        setEnergyUseKwhPerM2Yr(String(nextValue));
      }
      return;
    }

    if (previousProjectIdRef.current === projectId) return;
    previousProjectIdRef.current = projectId;

    if (Number.isFinite(nextValue)) {
      setEnergyUseKwhPerM2Yr(String(nextValue));
    } else if (!lcaData?.isSampleData) {
      setEnergyUseKwhPerM2Yr('');
    }
  }, [lcaData, energyUseKwhPerM2Yr]);

  useEffect(() => {
    updateGwpScrollIndicator();
  }, [activeTab, currentImpactData, lcaData, updateGwpScrollIndicator]);

  const handleApplyGridIntensityLookup = useCallback(({ country, year, valueKg }) => {
    if (!Number.isFinite(valueKg)) return;
    const rounded = Number(valueKg.toFixed(3));
    setGridIntensityKgPerKwh(rounded.toFixed(3));
    setGridIntensityLookupMeta({
      countryName: country?.name || 'dataset',
      countryCode: country?.code || '',
      year: year || '',
      valueKg: rounded
    });
  }, []);

  useEffect(() => {
    return () => {
      if (buildingInfoSaveTimeoutRef.current) {
        clearTimeout(buildingInfoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Hydrate from localStorage on first mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.lcaData) return;
      setLcaData(parsed.lcaData);
      hydrateEditableFromLca(parsed.lcaData);
      setRequireBuildingInfoCompletion(Boolean(parsed.requireBuildingInfoCompletion));
      setPreferLcaTabFirst(Boolean(parsed.preferLcaTabFirst));
    } catch (err) {
      console.error('Failed to hydrate dashboard state', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(ELECTRICITY_DATA_URL)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to fetch country list (${response.status})`);
        return response.text();
      })
      .then((csvText) => {
        if (cancelled) return;
        setCountryEntities(buildCountryEntities(csvText));
      })
      .catch((err) => {
        console.error('Failed to load country suggestions', err);
        setCountryEntities([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setCountryQuery((editableBuildingInfo.country || '').toString());
  }, [editableBuildingInfo.country]);

  // Persist LCA dashboard state
  useEffect(() => {
    if (lcaData) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          lcaData,
          requireBuildingInfoCompletion,
          preferLcaTabFirst,
        })
      );
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [lcaData, requireBuildingInfoCompletion, preferLcaTabFirst]);

  useEffect(() => {
    const isStartRoute = location.pathname === '/start';
    if (isStartRoute && lcaData && !requireBuildingInfoCompletion) {
      navigate('/dashboard', { replace: true });
    }
  }, [lcaData, location.pathname, navigate, requireBuildingInfoCompletion]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!countrySuggestionsRef.current) return;
      if (!countrySuggestionsRef.current.contains(event.target)) {
        setShowCountrySuggestions(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Validate file type
      const fileExt = file.name.toLowerCase().split('.').pop();
      if (!['xlsx', 'xls', 'csv'].includes(fileExt)) {
        throw new Error('Please upload an Excel (.xlsx, .xls) or CSV file');
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 20, 90));
      }, 100);

      // Parse Excel file using frontend parser
      const parser = new ExcelParser();
      const parsedData = await parser.parseFile(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Ensure buildingInfo exists with default values
      const validatedData = {
        ...parsedData,
        buildingInfo: {
          name: parsedData.buildingInfo?.name || 'LCA Project',
          area: parsedData.buildingInfo?.area || 1000,
          location: parsedData.buildingInfo?.location || 'Unknown',
          serviceLife: parsedData.buildingInfo?.serviceLife || 50,
          buildingType: parsedData.buildingInfo?.buildingType || 'office',
          energyUseKwhPerM2Yr:
            parsedData.buildingInfo?.energyUseKwhPerM2Yr ??
            parsedData.buildingInfo?.energyIntensityKwhPerM2Yr ??
            parsedData.buildingInfo?.energyImpactKwhPerM2Yr ??
            ''
        }
      };
      
      setLcaData(validatedData);
      setPreferLcaTabFirst(false);
      localStorage.removeItem('embodix_rhino_layers_v1');
      localStorage.removeItem('embodix_rhino_layer_categories_v1');
      
      // Set editable building info from validated data
      const { city, country } = getCityCountryFromLocation(validatedData.buildingInfo.location);
      setEditableBuildingInfo({
        name: validatedData.buildingInfo.name,
        area: validatedData.buildingInfo.area.toString(),
        city,
        country,
        buildingType: validatedData.buildingInfo.buildingType || 'office',
        serviceLife: validatedData.buildingInfo.serviceLife.toString()
      });
      setRequireBuildingInfoCompletion(true);
      setShowMissingFieldsMessage(false);
      setShowUploadSuccessAfterGating(true);
      setIsEditingBuildingInfo(false);
      
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.message || 'Failed to process file. Please check the file format and try again.');
      
      toast({
        title: "Upload Failed",
        description: error.message || 'Failed to process file',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const handleRhinoFileChange = async (event) => {
    const file = event.target.files?.[0];
    setRhinoFileName('');
    setRhinoFileError('');
    setRhinoFileReady(false);
    setRhinoFilePayload([]);

    if (!file) {
      return;
    }

    const fileName = file.name || '';
    setRhinoFileName(fileName);
    if (!fileName.toLowerCase().endsWith('.embodix')) {
      setRhinoFileError('Please upload a valid .embodix file.');
      return;
    }

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        throw new Error('Invalid .embodix content');
      }
      setRhinoFilePayload(parsed);
      setRhinoFileReady(true);
    } catch (err) {
      console.error('Failed to read .embodix file', err);
      setRhinoFileError('Unable to read this .embodix file. Please check the file format.');
      setRhinoFileReady(false);
    }
  };

  const buildRhinoPlaceholderData = () => {
    const impactCategories = mockLCAData.impactCategories || [];
    const impacts = {};
    impactCategories.forEach((category) => {
      impacts[category.id] = 0;
    });
    const totalImpacts = {};
    impactCategories.forEach((category) => {
      totalImpacts[category.id] = 0;
    });
    const trimmedName = rhinoFileName.replace(/\.embodix$/i, '').trim();
    const projectName = trimmedName || 'Rhino Import';

    return {
      buildingInfo: {
        name: projectName,
        area: 1000,
        location: 'Unknown',
        serviceLife: 50,
        buildingType: 'office',
      },
      impactCategories,
      components: [
        {
          id: `rhino_placeholder_${Date.now()}`,
          name: 'Rhino import',
          category: 'Rhino',
          description: 'Layer matcher data ready for mapping.',
          impacts,
        },
      ],
      totalImpacts,
      projectId: `project_${Date.now()}`,
      projectName,
      description: `Rhino integration session for ${projectName}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isRhinoImport: true,
    };
  };

  const handleRhinoStart = () => {
    if (!rhinoFileReady || rhinoFileError) return;
    localStorage.setItem('embodix_rhino_layers_v1', JSON.stringify(rhinoFilePayload));
    const rhinoData = buildRhinoPlaceholderData();
    setLcaData(rhinoData);
    hydrateEditableFromLca(rhinoData);
    setRequireBuildingInfoCompletion(true);
    setShowMissingFieldsMessage(false);
    setShowUploadSuccessAfterGating(false);
    setIsEditingBuildingInfo(false);
    setPreferLcaTabFirst(true);
    setShowSampleDataNotification(false);
    setRhinoDialogOpen(false);
  };

  const hydrateEditableFromLca = (data) => {
    const { city, country } = getCityCountryFromLocation(data.buildingInfo?.location);
    setEditableBuildingInfo({
      name: data.buildingInfo?.name || 'LCA Project',
      area: (data.buildingInfo?.area || 1000).toString(),
      city,
      country,
      buildingType: data.buildingInfo?.buildingType || 'office',
      serviceLife: (data.buildingInfo?.serviceLife || 50).toString()
    });
    setCountryQuery(country || '');
  };

  const handleUseSampleData = () => {
    // For demo purposes, we'll use the mock data
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('buildups-open-steps');
    }
    setGridIntensityKgPerKwh('');
    setGridIntensityLookupMeta(null);
    const sampleData = {
      buildingInfo: {
        name: mockLCAData.building.name,
        area: mockLCAData.building.area,
        location: mockLCAData.building.location,
        serviceLife: mockLCAData.building.serviceLife,
        energyUseKwhPerM2Yr: mockLCAData.building.energyUseKwhPerM2Yr || '',
        buildingType: mockLCAData.building.buildingType || 'office'
      },
      impactCategories: mockLCAData.impactCategories,
      components: mockLCAData.components,
      totalImpacts: mockLCAData.totalImpacts,
      projectId: `project_${Date.now()}`,
      projectName: mockLCAData.building.name,
      description: `LCA analysis for ${mockLCAData.building.name}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isSampleData: true // Mark this as sample data
    };

    setLcaData(sampleData);
    setPreferLcaTabFirst(false);
    localStorage.removeItem('embodix_rhino_layers_v1');
    localStorage.removeItem('embodix_rhino_layer_categories_v1');
    hydrateEditableFromLca(sampleData);
    setShowSampleDataNotification(true);
  };

  const handleStartEditing = () => {
    setIsEditingBuildingInfo(true);
    setShowMissingFieldsMessage(false);
    const { city, country } = getCityCountryFromLocation(lcaData.buildingInfo?.location);
    setEditableBuildingInfo({
      name: lcaData.buildingInfo?.name || 'LCA Project',
      area: (lcaData.buildingInfo?.area || 1000).toString(),
      city,
      country,
      buildingType: lcaData.buildingInfo?.buildingType || 'office',
      serviceLife: (lcaData.buildingInfo?.serviceLife || 50).toString()
    });
    setCountryQuery(country || '');
  };

  const finalizeBuildingInfoSave = ({ shouldShowUploadToast, gatingFlowActive }) => {
    buildingInfoSaveTimeoutRef.current = null;
    setShowMissingFieldsMessage(false);
    const combinedLocation = [editableBuildingInfo.city?.trim(), editableBuildingInfo.country?.trim()]
      .filter(Boolean)
      .join(', ') || 'Unknown';

    const updatedLcaData = {
      ...lcaData,
      buildingInfo: {
        ...lcaData.buildingInfo,
        name: editableBuildingInfo.name,
        area: parseFloat(editableBuildingInfo.area) || 1000,
        location: combinedLocation,
        buildingType: editableBuildingInfo.buildingType || 'office',
        serviceLife: parseInt(editableBuildingInfo.serviceLife) || 50
      }
    };

    setLcaData(updatedLcaData);
    setIsEditingBuildingInfo(false);
    setShowMissingFieldsMessage(false);
    if (gatingFlowActive) {
      setRequireBuildingInfoCompletion(false);
    }
    if (updatedLcaData?.isRhinoImport) {
      handleTabChange('lca');
      setRhinoReminderOpen(true);
    } else {
      handleTabChange('overview');
    }
    if (shouldShowUploadToast) {
      setShowUploadSuccessAfterGating(false);
      toast({
        title: "Success!",
        description: "LCA data has been processed and visualized successfully.",
      });
    } else {
      toast({
        title: "Building Info Updated",
        description: "Building information has been successfully updated.",
      });
    }
    setIsSavingBuildingInfo(false);
  };

  const handleSaveBuildingInfo = () => {
    const gatingFlowActive = requireBuildingInfoCompletion;
    const shouldShowUploadToast = showUploadSuccessAfterGating && gatingFlowActive;
    if (hasIncompleteBuildingInfo(editableBuildingInfo)) {
      setShowMissingFieldsMessage(true);
      return;
    }

    setIsSavingBuildingInfo(true);
    const runSave = () => finalizeBuildingInfoSave({ shouldShowUploadToast, gatingFlowActive });

    if (gatingFlowActive) {
      if (buildingInfoSaveTimeoutRef.current) {
        clearTimeout(buildingInfoSaveTimeoutRef.current);
      }
      buildingInfoSaveTimeoutRef.current = setTimeout(runSave, 650);
    } else {
      runSave();
    }
  };

  const handleCancelEditing = () => {
    setIsEditingBuildingInfo(false);
    setIsSavingBuildingInfo(false);
    const { city, country } = getCityCountryFromLocation(lcaData.buildingInfo?.location);
    setEditableBuildingInfo({
      name: lcaData.buildingInfo?.name || 'LCA Project',
      area: (lcaData.buildingInfo?.area || 1000).toString(),
      city,
      country,
      buildingType: lcaData.buildingInfo?.buildingType || 'office',
      serviceLife: (lcaData.buildingInfo?.serviceLife || 50).toString()
    });
    setCountryQuery(country || '');
  };

  const handleInputChange = (field, value) => {
    setEditableBuildingInfo(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      if (showMissingFieldsMessage && !hasIncompleteBuildingInfo(updated)) {
        setShowMissingFieldsMessage(false);
      }
      return updated;
    });
  };

  const filteredCountrySuggestions = useMemo(() => {
    const term = normalizeName(countryQuery);
    if (!term) {
      return countryEntities.slice(0, 15);
    }
    return countryEntities
      .filter(
        (entity) =>
          entity.nameLower.includes(term) ||
          (entity.codeLower && entity.codeLower.includes(term)),
      )
      .slice(0, 20);
  }, [countryEntities, countryQuery]);

  const handleCountrySelect = (entity) => {
    const name = entity?.name || '';
    setCountryQuery(name);
    handleInputChange('country', name);
    setShowCountrySuggestions(false);
  };

  // Convert kg to tonnes for display
  const convertToTonnes = (value) => {
    if (value >= 1000) {
      return (value / 1000).toFixed(2) + ' t';
    } else {
      return value.toLocaleString() + ' kg';
    }
  };

  if (requireBuildingInfoCompletion) {
    return (
      <div className="min-h-screen bg-[#d8dcd1] px-4 py-12 flex items-center justify-center">
        <div className="w-full max-w-3xl space-y-6 bg-white/95 border border-[#d5dacc] rounded-2xl shadow-lg p-6">
          <div className="text-center space-y-2">
            <span className="inline-flex items-center justify-center rounded-full border border-[#d5dacc] bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-[#2b4331]">
              EmbodiX
            </span>
            <h1 className="text-2xl font-semibold text-[#2b4331]">Confirm your project details</h1>
            <p className="text-sm text-[#4f6b5a]">
              We&apos;ll tailor ROI and benchmark modules based on this location and typology information.
            </p>
            {showMissingFieldsMessage && (
              <p className="text-xs text-red-600">
                Please fill in every field to continue.
              </p>
            )}
          </div>
          {renderBuildingInfoForm({
            hideCancel: true,
            primaryLabel: 'Continue to dashboard',
            primaryButtonClass: 'bg-[#2f6046] hover:bg-[#25503a] text-white',
            showInlineValidation: false,
            isSubmitting: isSavingBuildingInfo,
          })}
        </div>
      </div>
    );
  }

  if (!lcaData) {
    return (
      <div className="min-h-screen bg-[#e9ede3] px-4 py-8 flex items-center justify-center">
        <div className="w-full max-w-6xl space-y-8">
          <div className="space-y-1 text-center">
            <span className="inline-flex items-center justify-center rounded-full border border-[#d5dacc] bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-[#2b4331]">
              EmbodiX
            </span>
            <h1 className="text-1xl font-semibold text-[#2b4331] sm:text-2xl">Choose how you want to start</h1>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="border border-[#d5dacc] bg-white shadow-sm">
              <CardHeader className="space-y-4 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#2f6046] text-white">
                  <BarChart3 className="h-7 w-7" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-semibold text-[#2b4331]">Explore Sample</CardTitle>
                  <CardDescription className="mt-1 text-sm text-[#4f6b5a]">
                    Explore our sample dataset from a sustainable office building to discover the platform&apos;s full capabilities.
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  {[
                    { icon: BarChart3, label: 'Pre-loaded Building Data' },
                    { icon: TrendingUp, label: 'Instant Dashboard Preview' },
                    { icon: Grid3X3, label: 'Examine data visualization' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#d5dacc] bg-[#eef2e7] text-[#2b4331]">
                        <item.icon className="h-5 w-5" />
                      </div>
                      <span className="text-sm font-medium text-[#2b4331]">{item.label}</span>
                    </div>
                  ))}
                </div>

                <div className="rounded-xl border border-[#d5dacc] bg-[#eef2e7] p-4">
                  <h4 className="text-sm font-semibold text-[#2f6046]">Sample Dataset Includes:</h4>
                  <ul className="mt-3 space-y-1 text-sm text-[#2b4331]">
                    <li>• 20+ Building Components & Materials</li>
                    <li>• 5 Impact Categories (GWP, ODP, AP, etc.)</li>
                    <li>• Component Breakdown Analysis</li>
                    <li>• Impact flow Analysis</li>
                  </ul>
                </div>

                <Button
                  onClick={handleUseSampleData}
                  disabled={isLoading}
                  className="w-full bg-[#2f6046] text-white hover:bg-[#25503a]"
                >
                  View Sample
                </Button>
              </CardContent>
            </Card>

            <Card className="border border-[#d5dacc] bg-white shadow-sm">
              <CardHeader className="space-y-4 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#2f6046] text-white">
                  {isLoading ? <Loader2 className="h-7 w-7 animate-spin" /> : <Upload className="h-7 w-7" />}
                </div>
                <div>
                  <CardTitle className="text-2xl font-semibold text-[#2b4331]">
                    {isLoading ? 'Processing File…' : 'Upload Your Data'}
                  </CardTitle>
                  <CardDescription className="mt-1 text-sm text-[#4f6b5a]">
                    Upload your Excel file containing LCA results and component data to generate custom visualizations and insights.
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 text-[#2b4331]">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {isLoading && (
                  <div className="space-y-2">
                    <div className="h-2 w-full rounded-full bg-[#dfe5d8]">
                      <div
                        className="h-2 rounded-full bg-[#2f6046]"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-center text-xs text-[#4f6b5a]">{uploadProgress}% complete</p>
                  </div>
                )}

                <div className="space-y-4">
                  {[
                    { icon: BarChart3, label: 'Interactive Charts & Graphs' },
                    { icon: Target, label: 'Comprehensive Impact Analysis' },
                    { icon: Globe, label: 'Comparison with international and local targets' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3 text-sm text-[#4f6b5a]">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#d5dacc] bg-[#eef2e7] text-[#2b4331]">
                        <item.icon className="h-5 w-5" />
                      </div>
                      <span className="font-medium">{item.label}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col items-center gap-3">
                  <label
                    htmlFor="lca-upload"
                    className={`flex w-full cursor-pointer flex-col items-center rounded-xl border border-dashed border-[#d5dacc] bg-[#eef2e7] px-4 py-4 text-sm text-[#4f6b5a] text-center ${isLoading ? 'pointer-events-none opacity-60' : ''}`}
                  >
                    <Upload className="mb-2 h-5 w-5 text-[#2f6046]" />
                    Click to upload Excel file
                    <span className="mt-2 text-xs text-[#4f6b5a]">Supports .xlsx, .xls, .csv formats (max 10MB)</span>
                    <Input
                      id="lca-upload"
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileUpload}
                      disabled={isLoading}
                      className="hidden"
                    />
                  </label>
                  <Link
                    to="/template"
                    className="text-xs font-medium text-[#2f6046] underline underline-offset-4"
                  >
                    📄 View Documentation
                  </Link>
                  <p className="text-xs text-[#4f6b5a] text-center -mb-1">
                    Refer the documentation here to format the Excel and Rhino lyers for plugin integration.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                    className="w-full bg-[#2f6046] text-white hover:bg-[#25503a]"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        Processing…
                      </>
                    ) : (
                      <>
                        <Upload className="mr-1 h-4 w-4" />
                        Upload Dataset
                      </>
                    )}
                  </Button>
                  <Button
                    disabled={isLoading}
                    className="w-full bg-[#2f6046] text-white hover:bg-[#25503a]"
                    onClick={() => {
                      setRhinoDialogOpen(true);
                      setRhinoFileError('');
                      setRhinoFileName('');
                      setRhinoFileReady(false);
                      setRhinoFilePayload([]);
                    }}
                  >
                    <img
                      src="/assets/rhino-logo.png"
                      className="mr-1 h-5 w-5"
                      style={{ filter: 'brightness(0) invert(1)' }}
                      alt=""
                      aria-hidden="true"
                    />
                    Rhino Integration
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="rounded-2xl border border-[#d5dacc] bg-white px-4 py-3 text-center text-sm text-[#4f6b5a] -mt-4">
            Reminder: Uploaded data stays in your browser cache only. Clearing this site&apos;s cache will remove all progress.
          </div>

          <Dialog open={rhinoDialogOpen} onOpenChange={setRhinoDialogOpen}>
            <DialogContent className="w-[95vw] max-w-xl">
              <DialogHeader className="text-left text-[#2b4331]">
                <DialogTitle className="text-[#2b4331]">Rhino Integration</DialogTitle>
                <DialogDescription className="text-[#2b4331] text-justify">
                  Upload your .embodix export from the Rhino plugin to bring project data into EmbodiX.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="rhino-embodix-upload" className="text-[#2b4331]">
                    Upload .embodix file
                  </Label>
                  <div className="flex flex-col gap-2">
                    <Input
                      id="rhino-embodix-upload"
                      type="file"
                      accept=".embodix"
                      onChange={handleRhinoFileChange}
                      className="h-11 overflow-hidden p-0 text-[#2b4331] file:mr-3 file:h-full file:rounded-l-md file:rounded-r-none file:border-0 file:bg-[#2f6046] file:px-4 file:text-sm file:font-medium file:text-white hover:file:bg-[#25503a]"
                    />
                    {rhinoFileError && (
                      <p className="text-xs text-red-600">{rhinoFileError}</p>
                    )}
                  </div>
                </div>
                <div className="rounded-lg border border-[#d5dacc] bg-[#f3f6f1] p-3 text-sm text-[#2b4331] text-justify">
                  Download the EmbodiX plugin for Rhino from the Package Manager and Food4Rhino. Refer to the documentation to understand how to use the plugin for data integration.
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Button
                    type="button"
                    disabled={!rhinoFileReady || !!rhinoFileError}
                    className="w-full bg-[#2f6046] text-white hover:bg-[#25503a] disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={handleRhinoStart}
                  >
                    Start
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full border-[#2f6046] text-[#2f6046] hover:bg-[#e6f0ea]"
                  >
                    <Link
                      to="/template"
                      onClick={() => setRhinoDialogOpen(false)}
                    >
                      Documentation
                    </Link>
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-0.5">
          <div className="flex flex-col gap-y-1 py-0 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col items-center text-center gap-0.5 lg:hidden">
              <div className="flex items-center justify-center gap-1 -translate-x-1">
                <div
                  role="img"
                  aria-label="EmbodiX logo"
                  className="h-12 w-12 bg-[#245040]"
                  style={{
                    maskImage: "url('/assets/Logo.svg')",
                    maskPosition: 'center',
                    maskRepeat: 'no-repeat',
                    maskSize: 'contain',
                    WebkitMaskImage: "url('/assets/Logo.svg')",
                    WebkitMaskPosition: 'center',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskSize: 'contain',
                  }}
                />
                <h1 className="text-2xl font-bold text-[#245040]">EmbodiX</h1>
              </div>
              <p className="text-xs text-gray-600 -mt-2 mb-3">Environmental Impact Analysis</p>
            </div>

            <div className="hidden lg:flex lg:items-center lg:justify-start">
              <div
                role="img"
                aria-label="EmbodiX logo"
                className="h-16 w-16 lg:h-[4.5rem] lg:w-[4.5rem] bg-[#245040]"
                style={{
                  maskImage: "url('/assets/Logo.svg')",
                  maskPosition: 'center',
                  maskRepeat: 'no-repeat',
                  maskSize: 'contain',
                  WebkitMaskImage: "url('/assets/Logo.svg')",
                  WebkitMaskPosition: 'center',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskSize: 'contain',
                }}
              />
              <div className="text-left -mt-1">
                <h1 className="text-3xl font-bold text-[#245040]">EmbodiX</h1>
                <p className="text-sm text-gray-600 leading-tight whitespace-nowrap">Environmental Impact Analysis</p>
              </div>
            </div>

            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:flex-row sm:items-center sm:justify-end sm:gap-3 lg:gap-6">
              <Button 
                 variant="outline" 
                 size="sm"
                 onClick={() => {
                   const shouldReset = window.confirm(
                     'Navigating to the start page will lose all your progress. Do you want to continue?'
                   );
                 if (!shouldReset) return;
                   setLcaData(null);
                   setRequireBuildingInfoCompletion(false);
                   setPreferLcaTabFirst(false);
                   setEnergyUseKwhPerM2Yr('');
                   setGridIntensityKgPerKwh('');
                   setGridIntensityLookupMeta(null);
                   sessionStorage.removeItem('buildups-open-steps');
                   localStorage.removeItem(STORAGE_KEY);
                   localStorage.removeItem('embodix_rhino_layers_v1');
                   localStorage.removeItem('embodix_rhino_layer_categories_v1');
                   localStorage.removeItem('embodix_buildups_state_v1');
                   localStorage.removeItem('embodix_lca_mvp_state_v1');
                   localStorage.removeItem('embodix_custom_epds_v1');
                   clearSavedWalls();
                   navigate('/start');
                 }}
                 className="w-full sm:w-auto border-emerald-200 text-emerald-700 hover:bg-emerald-50"
               >
                 <Upload className="w-4 h-4 mr-2" />
                 New File
               </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                {/* Building Info Card */}
        {lcaData && (
          <>
          <Card className="mb-8 bg-white/80 backdrop-blur-sm border-0 shadow-md">
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex-1 text-center sm:text-left">
                  <div className="space-y-1 text-center sm:text-left">
                    <CardTitle className="text-xl text-gray-800">{lcaData.buildingInfo?.name || 'LCA Project'}</CardTitle>
                    <CardDescription className="text-gray-600 space-y-0.5">
                      <span>
                        {formatBuildingType(lcaData.buildingInfo?.buildingType) || 'N/A'} • {lcaData.buildingInfo?.area || 'N/A'} m² • {lcaData.buildingInfo?.serviceLife || 'N/A'} years service life
                      </span>
                      <span className="block text-sm text-gray-500">
                        Location: {lcaData.buildingInfo?.location || 'Unknown'}
                      </span>
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 sm:ml-4 sm:justify-end sm:self-start">
                  <Button
                    onClick={handleStartEditing}
                    size="sm"
                    variant="outline"
                    className="w-full sm:w-auto text-gray-600 hover:text-gray-800"
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    Edit Building Info
                  </Button>
                </div>
              </div>
            </CardHeader>
          
          {currentImpactData && (
            <CardContent>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg p-4 text-white">
                  <p className="text-sm opacity-90">Total Impact</p>
                  <p className="text-2xl font-bold">{convertToTonnes(currentImpactData.total)}</p>
                  <p className="text-xs opacity-75">t CO2-eq (displayed in tonnes)</p>
                </div>
                                <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg p-4 text-white">
                  <p className="text-sm opacity-90">Per m²/year</p>
                  <p className="text-2xl font-bold">
                    {lcaData.buildingInfo?.area && lcaData.buildingInfo?.serviceLife 
                      ? ((currentImpactData.total / lcaData.buildingInfo.area) / lcaData.buildingInfo.serviceLife).toFixed(2)
                      : 'N/A'
                    }
                  </p>
                  <p className="text-xs opacity-75">kg CO2-eq/m²/year</p>
                </div>
                <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg p-4 text-white">
                  <p className="text-sm opacity-90">Highest Component</p>
                  <p className="text-lg font-bold">
                    {currentImpactData.data.reduce((max, item) => 
                      item.value > max.value ? item : max
                    ).name}
                  </p>
                  <p className="text-xs opacity-75">
                    {currentImpactData.data.reduce((max, item) => 
                      item.value > max.value ? item : max
                    ).percentage.toFixed(1)}%
                  </p>
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg p-4 text-white">
                  <p className="text-sm opacity-90">Components</p>
                                     <p className="text-2xl font-bold">{lcaData.components.length}</p>
                  <p className="text-xs opacity-75">Analyzed</p>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
        <Dialog
          open={isEditingBuildingInfo}
          onOpenChange={(open) => {
            if (!open) {
              handleCancelEditing();
            }
          }}
        >
          <DialogContent className="w-[95vw] max-w-3xl">
            <DialogHeader className="items-center text-center">
              <DialogTitle className="w-full text-center text-[#0f0f0f]">Edit building information</DialogTitle>
              <DialogDescription className="w-full text-center">
                Provide location and typology details so we can tailor benchmarks and ROI modules.
              </DialogDescription>
            </DialogHeader>
            {renderBuildingInfoForm({ isSubmitting: isSavingBuildingInfo })}
          </DialogContent>
        </Dialog>
        </>
        )}

        {/* Analysis Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-8 -mt-4">
          <div className="sm:hidden">
            <Select value={activeTab} onValueChange={handleTabChange}>
              <SelectTrigger className="relative w-full bg-white/80 border border-emerald-200 text-black justify-center text-[15px] [&>svg]:absolute [&>svg]:right-3">
                <SelectValue placeholder="Select a view" className="block w-full text-center" />
              </SelectTrigger>
              <SelectContent>
                {orderedTabs.map((tab) => (
                  <SelectItem key={tab.value} value={tab.value} className="text-black justify-center text-center text-[15px]">
                    {tab.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <TabsList className="hidden w-full grid-cols-6 bg-white/60 backdrop-blur-sm border border-emerald-200 rounded-lg overflow-hidden divide-x divide-emerald-200 sm:grid">
            {orderedTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="bg-white/80 data-[state=active]:bg-emerald-500 data-[state=active]:text-white"
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {lcaData && (
              <ProjectInfo
                lcaData={lcaData}
                currentImpactData={currentImpactData}
                gwpScrollContainerRef={gwpScrollContainerRef}
                gwpScrollIndicator={gwpScrollIndicator}
                energyIntensityKwhPerM2Yr={energyIntensityForCrrem}
              />
            )}
            {lcaData && (
              <ConstructionCapacity
                location={lcaData.buildingInfo?.location}
                countryEntities={countryEntities}
              />
            )}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {impactByCategoryData && (
              <InteractivePieChart
                data={impactByCategoryData}
                title="Impact by Category"
                componentsByCategory={embodiedComponentsByCategory}
                buildingInfo={lcaData?.buildingInfo}
                contentClassName="pb-10 sm:pb-16"
                mobileChartScale="scale-95 sm:scale-100"
              />
            )}
              {currentImpactData && (
                embodiedOperationalChart ? (
                  <InteractivePieChart
                    data={embodiedOperationalChart}
                    title="Embodied vs Operational"
                    componentsByCategory={{}}
                    buildingInfo={lcaData?.buildingInfo}
                    hideCategoryLegend
                    hideComponentLegend
                    categoryDataOverride={embodiedOperationalChart.data}
                    contentClassName="pb-10 sm:pb-16"
                    mobileChartScale="scale-95 sm:scale-100"
                    onPopupOpenChange={handleEnergyPopupOpenChange}
                    onPopupCloseRequest={handleEnergyPopupCloseRequest}
                    closePopupSignal={energyPopupCloseNonce}
                    rightPanelOverride={(
                      <div className="space-y-4">
                        <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-900">
                          Enter energy inputs to estimate operational carbon (B6) in this overview split.
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <Label className="text-sm text-gray-600">
                              Consumption (kWh/m<sup>2</sup>/yr)
                            </Label>
                            <Input
                              value={energyUseKwhPerM2Yr}
                              onChange={(e) => setEnergyUseKwhPerM2Yr(e.target.value)}
                              inputMode="decimal"
                              className="mt-1 text-black placeholder:text-gray-400"
                              placeholder="e.g., 120"
                              disabled={hasB6StageInLca}
                            />
                          </div>
                          <div>
                            <Label className="text-sm text-gray-600">Grid intensity (kgCO2e/kWh)</Label>
                            <Input
                              value={gridIntensityKgPerKwh}
                              onChange={(e) => setGridIntensityKgPerKwh(e.target.value)}
                              inputMode="decimal"
                              className="mt-1 text-black placeholder:text-gray-400"
                              placeholder="e.g., 0.45"
                              disabled={hasB6StageInLca}
                            />
                            {gridIntensityLookupMeta ? (
                              <div className="mt-1 text-[11px] text-gray-500">
                                Last applied from {gridIntensityLookupMeta.countryName}
                                {gridIntensityLookupMeta.countryCode ? ` (${gridIntensityLookupMeta.countryCode})` : ''}
                                {gridIntensityLookupMeta.year ? ` - ${gridIntensityLookupMeta.year}` : ''}:{' '}
                                {gridIntensityLookupMeta.valueKg.toFixed(3)} kgCO2e/kWh. Change it for custom values.
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="space-y-2">
                          {embodiedOperationalChart.data.map((entry) => {
                            const perM2Yr = impactPerM2YrDenom ? entry.value / impactPerM2YrDenom : null;
                            return (
                              <div key={entry.name} className="flex items-center justify-between text-xs text-gray-600">
                                <div className="flex items-center gap-2">
                                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                                  <span className="font-medium">{entry.name}</span>
                                </div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-800">
                                  {perM2Yr != null ? `${perM2Yr.toFixed(2)} kgCO2e/m²/yr` : 'N/A'}
                                </span>
                                <span className="font-semibold text-gray-800">
                                  {entry.percentage.toFixed(1)}%
                                </span>
                              </div>
                              </div>
                            );
                          })}
                        </div>
                      <div className="space-y-2">
                        <div className="text-xs text-gray-600">
                          Need a grid factor? Apply the latest value from the lookup below.
                        </div>
                        {hasB6StageInLca && !lcaData?.isRhinoImport ? (
                          <div className="text-[11px] font-semibold text-red-600">
                            B6 values are already provided in the Excel file.
                          </div>
                        ) : null}
                        {lcaData?.isRhinoImport ? (
                          <div className="text-[11px] font-semibold text-red-600">
                            B6 values are sourced from the Whole Building LCA tab.
                          </div>
                        ) : null}
                        <div className="h-1" />
                        <div className="h-px w-full bg-gray-200" />
                        <div className="h-1" />
                        <div className="rounded-lg bg-white p-3">
                          <CarbonIntensityLookup onApplyValue={handleApplyGridIntensityLookup} />
                        </div>
                      </div>
                      </div>
                    )}
                  />
                ) : (
                  <Card className="border border-emerald-100 bg-white/80 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg text-gray-800">Embodied vs Operational</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600">Declare energy impact to view chart.</p>
                    </CardContent>
                  </Card>
                )
              )}
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {lcaData && (
                <LifeCycleChart
                  selectedImpact={selectedImpact}
                  lcaData={lcaData}
                  onSummaryData={setLifeCycleSummary}
                  b6OperationalImpact={operationalImpactForB6}
                  hasB6FromStages={hasB6StageInLca}
                />
              )}
              {lcaData && <MaterialComparison selectedImpact={selectedImpact} lcaData={lcaData} />}
            </div>
            {lcaData && lifeCycleSummary && (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg p-4 text-white">
                  <p className="text-sm opacity-90">Benefits by Reduction (Stage D)</p>
                  <p className="text-xl font-bold">
                    {lifeCycleSummary.totalNegativeImpact > 0 ? '-' : ''}
                    {lifeCycleSummary.totalNegativeImpact.toLocaleString()}
                  </p>
                  <p className="text-xs opacity-75">
                    {lifeCycleSummary.totalPositiveImpact > 0
                      ? ((lifeCycleSummary.totalNegativeImpact / lifeCycleSummary.totalPositiveImpact) * 100).toFixed(1)
                      : '0.0'}% reduction
                  </p>
                </div>

                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg p-4 text-white">
                  <p className="text-sm opacity-90">Total Impact (without Benefits)</p>
                  <p className="text-2xl font-bold">
                    {(lifeCycleSummary.totalImpactWithoutBenefits / 1000).toFixed(2)} t
                  </p>
                  <p className="text-xs opacity-75">Total impact excluding benefits (negative D)</p>
                </div>

                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg p-4 text-white">
                  <p className="text-sm opacity-90">Carbon Intensity</p>
                  <p className="text-2xl font-bold">
                    {lifeCycleSummary.carbonIntensity.toFixed(2)} t/m²
                  </p>
                  <p className="text-xs opacity-75">Per square meter</p>
                </div>

                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg p-4 text-white">
                  <p className="text-sm opacity-90">Highest Stage</p>
                  <p className="text-xl font-bold">{lifeCycleSummary.highestStageName}</p>
                  <p className="text-xs opacity-75">{lifeCycleSummary.highestStageFullName}</p>
                </div>
              </div>
            )}
          </TabsContent>


          <TabsContent value="breakdown" className="space-y-6">
            {lcaData && <ComponentBreakdown 
              components={lcaData.components}
              selectedImpact={selectedImpact}
              componentsByCategory={componentsByCategory}
              lcaData={lcaData}
            />}
          </TabsContent>

          <TabsContent value="data-visualization" className="space-y-6">
            {lcaData && (
              <>
                <MatrixHeatmap 
                  selectedImpact={selectedImpact} 
                  lcaData={lcaData}
                  expandMap={heatmapExpandMap}
                  setExpandMap={setHeatmapExpandMap}
                  visibleStages={heatmapVisibleStages}
                  setVisibleStages={setHeatmapVisibleStages}
                  hideValues={heatmapHideValues}
                  setHideValues={setHeatmapHideValues}
                />
                <SankeyDiagram selectedImpact={selectedImpact} lcaData={lcaData} />
              </>
            )}
          </TabsContent>

          <TabsContent value="pvroi" className="space-y-6">
            <div className={activeRoiView === 'menu' ? 'space-y-6' : 'hidden'}>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {ROI_CARD_OPTIONS.map((card) => {
                  const Icon = card.icon;
                  const disabledClasses = card.disabled
                    ? 'cursor-not-allowed opacity-60 border-dashed border-gray-200 bg-gray-50'
                    : 'hover:border-[#2f6046] focus-visible:ring-2 focus-visible:ring-[#2f6046]';

                  return (
                    <button
                      type="button"
                      key={card.id}
                      disabled={card.disabled}
                      onClick={() => !card.disabled && setActiveRoiView(card.id)}
                      className={`group flex h-full flex-col rounded-2xl border border-emerald-100 p-5 text-left transition duration-200 bg-white/80 backdrop-blur-sm ${disabledClasses}`}
                      style={{ transformOrigin: 'center', transition: 'transform 240ms ease, border-color 200ms ease' }}
                      onMouseEnter={(e) => {
                        if (!card.disabled) e.currentTarget.style.transform = 'scale(1.02)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                          <Icon className="h-6 w-6" />
                        </div>
                        <span className={`text-xs font-semibold uppercase tracking-wide ${card.disabled ? 'text-gray-400' : 'text-emerald-600'}`}>
                          {card.statusLabel}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="text-lg font-semibold text-gray-900">{card.title}</p>
                        <p className="mt-2 text-sm text-gray-600">{card.description}</p>
                      </div>
                      <span
                        className={`mt-4 inline-flex items-center text-sm font-medium ${card.disabled ? 'text-gray-400' : 'text-emerald-700 group-hover:text-emerald-900'}`}
                      >
                        {card.disabled ? 'In progress' : 'Calculate →'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={activeRoiView === 'menu' ? 'hidden' : 'space-y-4'}>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveRoiView('menu')}
                  className="pl-0 text-emerald-700 hover:text-emerald-900"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  ROI modules
                </Button>
              </div>

              <div className={activeRoiView === 'pv-carbon' ? 'space-y-4' : 'hidden'}>
                <PVROIAnalysis />
              </div>

              {activeRoiView !== 'menu' && activeRoiView !== 'pv-carbon' && (
                <Card className="bg-white/80 backdrop-blur-sm border border-dashed border-emerald-200">
                  <CardHeader>
                    <CardTitle className="text-gray-800">Module coming soon</CardTitle>
                    <CardDescription>
                      We&apos;re still wiring up this ROI workflow. Check back soon or start with PV Carbon ROI above.
                    </CardDescription>
                  </CardHeader>
                </Card>
              )}
            </div>
          </TabsContent>
          <TabsContent value="buildups" className="space-y-6" forceMount>
            <Buildups />
          </TabsContent>
          <TabsContent value="lca" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border border-[#d5dacc] bg-white shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-[#2b4331]">
                    Quick LCA
                    <Badge variant="outline" className="border-[#d5dacc] bg-[#eef2e7] text-[#2b4331] text-xs">
                      Coming soon
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-[#4f6b5a]">
                    Rapid concept-level estimates for early design decisions. We&apos;re putting this workflow together-check back soon.
                  </p>
                  <Button type="button" variant="outline" disabled className="w-full cursor-not-allowed opacity-70 border-[#d5dacc]">
                    Launch Quick LCA
                  </Button>
                </CardContent>
              </Card>

              <Card className="border border-[#d5dacc] bg-white shadow-sm">
                <CardHeader>
                  <CardTitle className="text-[#2b4331]">Whole building LCA</CardTitle>
                  <CardDescription>
                    Detailed component-based workflow with unit conversion, service life replacements, and GWP breakdowns.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="text-sm text-[#4f6b5a] list-disc list-inside space-y-1">
                    <li>Shortlist EPDs from multiple database</li>
                    <li>Add components by category</li>
                    <li>Customization and creation of own building components</li>
                    <li>Whole-life stage results and charts</li>
                  </ul>
                  <Link to="/wholebuildinglca" className="block">
                    <Button type="button" className="w-full bg-[#2f6046] text-white hover:bg-[#25503a]">
                      Launch Whole Building LCA
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Centered Sample Data Notification */}
      {showSampleDataNotification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-3 sm:px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs sm:max-w-md md:max-w-lg lg:max-w-xl px-5 py-7 sm:px-7 sm:py-8 transform transition-all duration-300 scale-100">
            <div className="text-center space-y-4 sm:space-y-5">
              <div className="mx-auto w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                <Leaf className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <h3 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900">Sample Data Loaded</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed">
                You're now viewing sample LCA data. Change country in the building info card to view country specific data and targets. Upload your own file to analyze real data.
              </p>
              <Button
                onClick={() => setShowSampleDataNotification(false)}
                className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-5 py-2 sm:px-7 sm:py-2.5 rounded-lg font-medium"
              >
                Got it!
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={energyConfirmOpen} onOpenChange={setEnergyConfirmOpen}>
        <AlertDialogContent className="max-w-md bg-white text-[#2b4331]">
          <AlertDialogHeader>
            <AlertDialogTitle>Save energy input changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#4f6b5a]">
              You have unsaved changes to consumption or grid intensity.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-8 border-[#d5dacc] bg-white text-[#2b4331] hover:bg-[#eef2e7]"
              onClick={handleEnergyConfirmDiscard}
            >
              Discard
            </Button>
            <Button
              type="button"
              className="h-8 bg-[#2f6046] text-white hover:bg-[#25503a]"
              onClick={handleEnergyConfirmSave}
            >
              Save
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={rhinoReminderOpen} onOpenChange={setRhinoReminderOpen}>
        <DialogContent className="w-[95vw] max-w-lg">
          <DialogHeader className="text-left text-[#2b4331]">
            <DialogTitle>Next step: Whole Building LCA</DialogTitle>
            <DialogDescription className="text-[#4f6b5a]">
              Go inside the Whole Building LCA tab to fill the Layer Matcher and calculate results. Only when that is complete,
              the overview and other tabs will show the results.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button
              type="button"
              className="bg-[#2f6046] text-white hover:bg-[#25503a]"
              onClick={() => setRhinoReminderOpen(false)}
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LCADashboard;

